import { config, Config } from '../config';

const currentOrigin = () => window.location.origin;

let configCache: Config;
const getConfig = () => {
  if (typeof configCache !== 'undefined') return configCache;
  const origin = currentOrigin();

  let conf = getConfigByOrigin(config, origin);

  // Falls back to `local` for any origin with no entry above, which personal dev stacks rely on
  // (the `dev` entry keeps a literal `{user}` placeholder, so it never matches). Note that
  // `config.local` points `smithyApi` at alpha: an origin that is missing an entry does not fail
  // loudly, it silently calls the alpha API and dies on CORS. A new stage or ITL target must be
  // added to `config` or it will look like an alpha routing bug.
  if (!conf) {
    conf = config.local;
  }

  configCache = conf;

  return conf;
};

export const getStageByOrigin = () => getStageForOrigin(currentOrigin());

/** The config stage whose URLs match `origin`, or null when none does. */
export const getStageForOrigin = (origin: string): string | null => {
  const stages = Object.entries(config)
    .filter(([_, entry]) => {
      return entry.urls.some((url) => {
        return origin.includes(url);
      });
    })
    .map(([stage, _]) => stage);

  return stages.length > 0 ? stages[0] : null;
};

const REVIEW_MOCK_STAGES = ['local', 'dev', 'alpha'];
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];
// Personal dev stacks are served from <alias> under this domain and have no entry in config.
const PERSONAL_STACK_HOST_SUFFIX = '.red-velvet-internal-prototype.aitc.gca.amazon.dev';

/**
 * Whether a page may show mock data while its API is not built yet: on local, dev, alpha and ITL
 * stages, and on a local or personal-stack host. Fails closed, so beta, prod and any origin it does
 * not recognize never get mocks, even if a feature flag is turned on there.
 */
export const areReviewMocksAllowedFor = (origin: string): boolean => {
  const stage = getStageForOrigin(origin);
  if (stage !== null) {
    return REVIEW_MOCK_STAGES.includes(stage) || stage.endsWith('-itl');
  }
  let hostname: string;
  try {
    hostname = new URL(origin).hostname;
  } catch {
    return false;
  }
  return LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith(PERSONAL_STACK_HOST_SUFFIX);
};

export const areReviewMocksAllowed = () => areReviewMocksAllowedFor(currentOrigin());

export const getConfigByOrigin = (config: { [key: string]: Config }, origin: string) => {
  const configEntries = Object.values(config).filter((entry) => {
    return entry.urls.some((url) => {
      return origin.includes(url);
    });
  });

  return configEntries.length > 0 ? configEntries[0] : null;
};

// Note: This is only used when retrieving local dummy API files
export const getSiteUrl = () => {
  const urls = getConfig().urls;
  const matched = urls.reduce((matchedUrl: string | null, url: string) => {
    if (matchedUrl) return matchedUrl;
    return currentOrigin().includes(url) || url.includes(currentOrigin()) ? url : null;
  }, null);
  return matched;
};

export const getApiUrl = () => getConfig().api;

export const getSmithyApiUrl = () => getConfig().smithyApi;

export const getAnalyticsConfig = () => getConfig().analytics;

export const getAuthConfig = () => getConfig().auth;
